import { Sparkles, Heart, Crown, Award } from "lucide-react";
import { BUSINESS, ABOUT_IMAGE } from "@/data/salonData";
import { Reveal } from "@/lib/useReveal";

const PILLARS = [
  { icon: Crown, title: "Luxury Service", desc: "Every chair, every product, every moment is curated for a premium feel." },
  { icon: Heart, title: "Made For Women", desc: "A safe, calming, feminine space designed by a woman, for women." },
  { icon: Award, title: "Certified Artistry", desc: "Trained under industry-leading masters to deliver pro-level results." },
  { icon: Sparkles, title: "Luxury Products", desc: "L'Oréal, Schwarzkopf, Kaya, O3+, Dermalogica — only premium brands." },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-20 md:py-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Image */}
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#f9d0da]/40 to-[#d4af37]/20 rounded-3xl blur-2xl" />
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-[0_30px_60px_-15px_rgba(217,138,153,0.35)]">
                <img
                  src={ABOUT_IMAGE}
                  alt="Anshi's Makeover artistry"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Owner card */}
              <div className="absolute -bottom-6 right-4 sm:right-8 glass rounded-2xl p-4 max-w-[220px] shadow-xl">
                <div className="text-[10px] uppercase tracking-wider text-[#d4af37] font-semibold">
                  Founder
                </div>
                <div className="font-display text-xl text-[#2a1b18] font-medium leading-tight">
                  {BUSINESS.owner}
                </div>
                <div className="text-xs text-[#5c4642] mt-1">
                  Lead Makeup Artist
                </div>
              </div>
            </div>
          </Reveal>

          {/* Text */}
          <div className="lg:col-span-7">
            <Reveal>
              <span className="heading-flourish">About The Studio</span>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2a1b18] font-medium mt-4 leading-[1.05]">
                Traditional Luxury, <br />
                <span className="text-gradient-rose italic">Modern Glow.</span>
              </h2>
            </Reveal>

            <Reveal delay={120}>
              <p className="mt-6 text-[#5c4642] text-base md:text-lg leading-relaxed">
                {BUSINESS.name} is a premium beauty destination tucked in the
                heart of Freeganj, Ujjain — where heritage charm meets modern
                luxury. Founded by{" "}
                <strong className="text-[#b56477]">{BUSINESS.owner}</strong>, a
                certified makeup artist trained under national-level masters,
                the studio celebrates every woman who walks through the door.
              </p>
              <p className="mt-4 text-[#5c4642] text-base leading-relaxed">
                From your first haircut to your bridal day, expect intuitive
                hands, world-class products, and a calming sanctuary that feels
                like home — only prettier.
              </p>
            </Reveal>

            {/* Pillars */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4">
              {PILLARS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <Reveal key={p.title} delay={i * 100} className="">
                    <div className="card-lift bg-white border border-[#d98a99]/15 rounded-2xl p-5">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#fff0f5] to-[#f9d0da] flex items-center justify-center mb-3">
                        <Icon size={18} className="text-[#b56477]" />
                      </div>
                      <div className="font-display text-lg text-[#2a1b18] font-semibold">
                        {p.title}
                      </div>
                      <div className="text-sm text-[#5c4642] mt-1 leading-relaxed">
                        {p.desc}
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
